import { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3, Loader2 } from 'lucide-react';
import type { EngagementSeries } from '@/client/types/promotion';
import { numberFmt } from './atoms';
import { toEngagementSeries } from './listingMetrics';

const COLORS = ['#166534', '#84cc16', '#0ea5e9', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];

/** The two series admins look at first; the rest start hidden but toggleable. */
const DEFAULT_ON = ['impression', 'view'];

/**
 * Engagement is only exposed as monthly counts per engagement type for one
 * calendar year (`/admin/entities/:eid/engagement/monthly`) — there's no daily
 * or per-package breakdown behind it, so the range control is a year picker.
 */
export const PackageEngagementAnalytics: React.FC<{
  series: EngagementSeries[];
  year: number;
  onYear: (year: number) => void;
  maxYear: number;
  isLoading?: boolean;
  isFetching?: boolean;
}> = ({ series, year, onYear, maxYear, isLoading, isFetching }) => {
  const [hidden, setHidden] = useState<Set<number>>(new Set());

  // Only types that recorded something get a bar, a chip or a legend entry; the
  // rows still carry their zero months so the tooltip can read them out.
  const { rows, series: charted } = useMemo(() => toEngagementSeries(series), [series]);

  // Default the toggles once the year's series are known.
  useEffect(() => {
    if (!charted.length) return;
    setHidden(new Set(charted.filter((s) => !DEFAULT_ON.includes(s.name_system)).map((s) => s.engtid)));
  }, [charted]);

  const shown = useMemo(() => charted.filter((s) => !hidden.has(s.engtid)), [charted, hidden]);

  const colorOf = (engtid: number) => COLORS[charted.findIndex((s) => s.engtid === engtid) % COLORS.length];
  const hasData = charted.length > 0;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">Engagement analytics</h3>
          <span className="text-xs text-gray-400">monthly</span>
          {isFetching && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
        </div>
        <div className="inline-flex items-center rounded-md border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => onYear(year - 1)}
            className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
            aria-label="Previous year"
          >
            ‹
          </button>
          <span className="min-w-[3rem] px-1 text-center text-xs font-medium text-gray-700">{year}</span>
          <button
            type="button"
            onClick={() => onYear(year + 1)}
            disabled={year >= maxYear}
            className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            aria-label="Next year"
          >
            ›
          </button>
        </div>
      </div>

      {charted.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {charted.map((s) => {
            const on = !hidden.has(s.engtid);
            return (
              <button
                key={s.engtid}
                type="button"
                onClick={() =>
                  setHidden((prev) => {
                    const next = new Set(prev);
                    if (next.has(s.engtid)) next.delete(s.engtid);
                    else next.add(s.engtid);
                    return next;
                  })
                }
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                  on ? 'bg-gray-50 text-gray-700 ring-gray-300' : 'bg-white text-gray-400 ring-gray-200 hover:bg-gray-50'
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: on ? colorOf(s.engtid) : '#d1d5db' }}
                />
                {s.name}
                <span className="tabular-nums text-[10px] text-gray-400">{numberFmt.format(s.total)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="relative">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis hide allowDecimals={false} />
              <RechartsTooltip
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                formatter={(v: number, name: string) => [numberFmt.format(v), name]}
              />
              {shown.map((s) => (
                <Bar
                  key={s.engtid}
                  dataKey={s.name}
                  fill={colorOf(s.engtid)}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={26}
                  minPointSize={(value: number | null | undefined) => ((value ?? 0) > 0 ? 2 : 0)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {!hasData && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-400">
              {isLoading ? 'Loading engagement…' : `No engagement recorded in ${year}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
